/* eslint-disable react-refresh/only-export-components */
import { createBrowserRouter, redirect, RouteObject } from 'react-router-dom'
import loadable from '@loadable/component'
import {
  getAccessToken,
  getLoginUrl,
  getRefreshToken,
  hasToken,
  refreshToken,
} from '~/api'
import { PageBoundary, RouteBoundary } from '~/components/error-boundary'

const loginLoader = async () => {
  if (hasToken()) {
    return redirect('/')
  }
  return null
}

const protectedLoader = async () => {
  let token = getAccessToken()
  if (token) return token
  token = getRefreshToken()
  if (token) {
    try {
      return await refreshToken()
    } catch {}
  }
  return redirect(getLoginUrl(window.location))
}

/* layouts */
const BaseLayout = loadable(() => import('~/pages/layouts/BaseLayout'))
const EntryLayout = loadable(() => import('~/pages/layouts/EntryLayout'))
const KnowledgeLayout = loadable(
  () => import('~/pages/knowledge/KnowledgeLayout')
)

/* pages */
const Dashboard = loadable(() => import('~/pages/Dashboard'))
const LoginPage = loadable(() => import('~/pages/auth/LoginPage'))
const ForgotPasswordPage = loadable(
  () => import('~/pages/auth/ForgotPasswordPage')
)
const ResetPasswordPage = loadable(
  () => import('~/pages/auth/ResetPasswordPage')
)
const Surveys = loadable(() => import('~/pages/knowledge/surveys/Surveys'))
const SurveyResponses = loadable(
  () => import('~/pages/knowledge/surveys/Responses')
)
const SurveyResponseDetails = loadable(
  () => import('~/pages/knowledge/surveys/ResponseDetails')
)
const SurveySections = loadable(
  () => import('~/pages/knowledge/surveys/Sections')
)
const SurveyQuestions = loadable(
  () => import('~/pages/knowledge/surveys/Questions')
)
const SurveyAnswers = loadable(
  () => import('~/pages/knowledge/surveys/Answers')
)
const Scales = loadable(() => import('~/pages/knowledge/scales/Scales'))
const ScaleDetails = loadable(
  () => import('~/pages/knowledge/scales/ScaleDetails')
)
const ScaleResponses = loadable(
  () => import('~/pages/knowledge/scales/Responses')
)
const ScaleResponseDetails = loadable(
  () => import('~/pages/knowledge/scales/ResponseDetails')
)
const Users = loadable(() => import('~/pages/users/Users'))
const Groups = loadable(() => import('~/pages/groups/Groups'))

const commonBaseRouteProps = {
  loader: protectedLoader,
  errorElement: <PageBoundary />,
}

interface RouteNode extends Omit<RouteObject, 'children'> {
  name?: string
  type?: string
  children?: RouteNode[]
}

const routeList: RouteNode[] = [
  {
    element: <BaseLayout />,
    // id: 'root',
    errorElement: <RouteBoundary />,
    loader: protectedLoader,
    path: '/',
    children: [
      {
        ...commonBaseRouteProps,
        path: '',
        element: <Dashboard />,
      },
      {
        ...commonBaseRouteProps,
        type: 'knowledge',
        // path: 'knowledge',
        element: <KnowledgeLayout />,
        children: [
          {
            ...commonBaseRouteProps,
            path: 'knowledge/surveys',
            element: <Surveys />,
          },
          {
            ...commonBaseRouteProps,
            path: 'knowledge/surveys/:id',
            element: <SurveySections />,
            children: [
              {
                ...commonBaseRouteProps,
                path: 'sections/:sectionId',
                element: <SurveyQuestions />,
                children: [
                  {
                    ...commonBaseRouteProps,
                    path: 'questions/:questionId',
                    element: <SurveyAnswers />,
                  },
                ],
              },
            ],
          },
          {
            ...commonBaseRouteProps,
            path: 'knowledge/surveys/:surveyId/responses',
            element: <SurveyResponses />,
          },
          {
            ...commonBaseRouteProps,
            path: 'knowledge/surveys/:surveyId/responses/:responseId',
            element: <SurveyResponseDetails />,
          },
          {
            ...commonBaseRouteProps,
            path: 'knowledge/scales',
            element: <Scales />,
          },
          {
            ...commonBaseRouteProps,
            path: 'knowledge/scales/:id',
            element: <ScaleDetails />,
          },
          {
            ...commonBaseRouteProps,
            path: 'knowledge/scales/:scaleId/responses',
            element: <ScaleResponses />,
          },
          {
            ...commonBaseRouteProps,
            path: 'knowledge/scales/:scaleId/responses/:responseId',
            element: <ScaleResponseDetails />,
          },
        ],
      },
      {
        ...commonBaseRouteProps,
        type: 'administration',
        children: [
          {
            ...commonBaseRouteProps,
            path: 'users',
            type: 'users',
            element: <Users />,
          },
          {
            ...commonBaseRouteProps,
            path: 'groups',
            type: 'groups',
            element: <Groups />,
          },
        ],
      },
    ],
  },
  {
    element: <EntryLayout />,
    errorElement: <RouteBoundary />,
    path: '/',
    children: [
      {
        path: 'login',
        loader: loginLoader,
        element: <LoginPage />,
      },
      {
        path: 'forgot-password',
        loader: loginLoader,
        element: <ForgotPasswordPage />,
      },
      {
        path: 'reset-password/:resetToken',
        loader: loginLoader,
        element: <ResetPasswordPage />,
      },
    ],
  },
]

export interface NavigationNode extends RouteNode {
  id: string
  type?: string
  path?: string
  children?: NavigationNode[]
}

let navId = 0
const createNavigationNode = (
  level: number,
  prefix: string,
  child: RouteNode
): NavigationNode | undefined => {
  if (/\/:/.test(child.path ?? '')) return
  const node: NavigationNode = {
    id: `nav_${++navId}`,
  }
  const type = child.type ?? child.path?.replace(/^([^/]+\/)+/, '')
  if (type) node.type = type
  if (child.path) {
    node.path = `${prefix}/${child.path}`
  }
  if (level < 1) {
    const children = child.children?.map((child) => {
      return createNavigationNode(level + 1, node?.path ?? prefix, child)
    })
    if (children) {
      node.children = children.filter(
        (c): c is NavigationNode => c !== undefined
      )
    }
  }
  return node
}

export const navigation = routeList[0]
  .children!.filter(({ type, path }) => type || !!path)
  .map((child) => createNavigationNode(0, '', child))

export const router = createBrowserRouter(routeList as RouteObject[])
